import React, { useState } from 'react';
import { Printer, Eye, EyeOff } from 'lucide-react';
import SpeechDataEntry from './speech/SpeechDataEntry';
import SpeechReportModal from './speech/SpeechReportModal';

/**
 * SpeechPanel — top-level Speech Audiometry tab.
 *
 * Rebuilt on 2026-02-19 to replace the flat SRT/WR/WRN/MCL grid with a
 * three-subtest editor (SRT · WRS · PB Max) that includes interactive
 * graphs with two-way sync to their tables, auto-derived PB Max from
 * WRS peak, and a print-ready A4 report matching the Audinexa
 * reference asset.
 *
 * 2026-02-19 · Added "Preview print sizing" toggle so audiologists can
 * see the exact A4-report pill/graph dimensions inline — no need to
 * open the modal just to verify how the SRT / PB Max labels will look
 * on the printed page.
 */
const SpeechPanel = ({ data, onChange, patient, sessionId, audiologistName }) => {
  const [showReport, setShowReport] = useState(false);
  const [printPreview, setPrintPreview] = useState(false);
  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="speech-tab-root">
      <div className="flex items-center justify-between bg-slate-100 border-b border-slate-300 px-3 py-1 flex-shrink-0">
        <div className="text-xs font-semibold text-slate-700 uppercase flex items-center gap-2">
          Speech Audiometry · SRT | WRS | Speech Discrimination (PB Max)
          {printPreview && (
            <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100 border border-indigo-200 px-1.5 py-0.5 rounded normal-case" data-testid="speech-preview-badge">
              Print-size preview · read-only
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPrintPreview((v) => !v)}
            data-testid="speech-toggle-print-preview"
            aria-pressed={printPreview}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded shadow-sm border ${printPreview ? 'bg-indigo-600 border-indigo-700 text-white hover:bg-indigo-700' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
            title="Toggle A4-print sizing preview to verify pill labels and graph dimensions before printing">
            {printPreview ? <EyeOff size={12} /> : <Eye size={12} />}
            {printPreview ? 'Exit Preview' : 'Preview Print Sizing'}
          </button>
          <button type="button" onClick={() => setShowReport(true)}
            data-testid="speech-print-report-btn"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold bg-slate-800 hover:bg-slate-900 text-white rounded shadow-sm"
            title="Preview + print the standalone Speech Audiometry report (A4)">
            <Printer size={12} /> Print Speech Audiometry Report
          </button>
        </div>
      </div>
      <SpeechDataEntry data={data} onChange={onChange} audiologistName={audiologistName} printPreview={printPreview} />
      {showReport && (
        <SpeechReportModal data={data} patient={patient} sessionId={sessionId} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
};

export default SpeechPanel;
